import { ParamMap, Router } from '@angular/router';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import {
  PANEL_REGISTRY,
  isStackParam,
  getStackParamIndex,
  parseStackEntry,
  isStackablePanelType,
} from './side-panel-config';
import { SidePanelConfig, SidePanelViewModel } from './types/side-panel';

/**
 * Manages side panel state for STACKABLE panels (resource, violation) based on URL query params.
 *
 * Supports two URL formats for stackable panels:
 * 1. Single panel: ?integration=abc&resource=id (or &violation=id)
 * 2. Stacked panels: ?integration=abc&s0=resource:id1&s1=violation:id2&s2=resource:id3
 *
 * The stacked format (s0, s1, s2...) preserves order explicitly.
 *
 * NON-STACKABLE panels (issue, finding, agentStep, etc.) are NOT handled by this class.
 * They are handled directly by the SidePanelService.
 */
export class SidePanelQueryParamsState {
  private _integration: string | null = null;
  private _stackEntries: Map<number, { type: string; id: string }> = new Map();
  private _singlePanelType: string | null = null;
  private _singlePanelId: string | null = null;
  private _viewModel: SidePanelViewModel | undefined;

  public get viewModel() {
    return this._viewModel;
  }

  public get integration() {
    return this._integration;
  }

  /**
   * Check if the current state represents a stackable panel configuration.
   * Returns false for non-stackable panels (issue, finding, etc.)
   */
  public hasStackablePanels(): boolean {
    if (this._stackEntries.size > 0) {
      return true;
    }
    return this._singlePanelType !== null && this._singlePanelId !== null;
  }

  /**
   * Get panels to open based on current URL state.
   * Only returns stackable panels (resource, violation).
   * Returns empty array for non-stackable panels.
   */
  public getSidePanelsToOpen(): SidePanelConfig[] {
    if (!this._integration) {
      return [];
    }

    // Check for stacked panels first (s0, s1, s2...)
    if (this._stackEntries.size > 0) {
      return this._buildStackedPanels();
    }

    // Fall back to single panel format (resource=id or violation=id)
    if (this._singlePanelType && this._singlePanelId) {
      return this._buildSinglePanel();
    }

    return [];
  }

  /**
   * Get current route state for URL manipulation
   */
  public getRouteState(): Record<string, string | null> {
    const state: Record<string, string | null> = {
      [QueryParams.integration]: this._integration,
    };

    // Add stack entries
    for (const [index, entry] of this._stackEntries) {
      state[`s${index}`] = `${entry.type}:${entry.id}`;
    }

    // Add single panel params if no stack
    if (this._stackEntries.size === 0) {
      if (this._singlePanelType === 'resource') {
        state[QueryParams.resource] = this._singlePanelId;
      } else if (this._singlePanelType === 'violation') {
        state[QueryParams.violation] = this._singlePanelId;
      }
    }

    return state;
  }

  /**
   * Get empty route state for clearing all panels (both stackable and non-stackable)
   */
  public getEmptyRouteState(): Record<string, null> {
    const state: Record<string, null> = {
      // Stackable panel params
      [QueryParams.integration]: null,
      [QueryParams.resource]: null,
      [QueryParams.violation]: null,
      // Non-stackable panel params
      [QueryParams.issue]: null,
      [QueryParams.finding]: null,
      [QueryParams.agentStep]: null,
      [QueryParams.inLineControl]: null,
      [QueryParams.dataItem]: null,
      [QueryParams.redTeamSession]: null,
      // Additional params used by non-stackable panels
      stepThreadId: null,
      inline: null,
      preventionId: null,
      violationRule: null,
      policy: null,
      ruleType: null,
      findingThreadId: null,
      sourcePipeline: null,
    };

    // Clear all stack params
    for (const index of this._stackEntries.keys()) {
      state[`s${index}`] = null;
    }

    return state;
  }

  /**
   * Get the next available stack index
   */
  public getNextStackIndex(): number {
    if (this._stackEntries.size === 0) {
      // If we have a single panel, start stacking at s1
      // (the single panel will be converted to s0)
      return this._singlePanelType ? 1 : 0;
    }
    const maxIndex = Math.max(...this._stackEntries.keys());
    return maxIndex + 1;
  }

  /**
   * Check if we're currently in stacking mode
   */
  public isStackingMode(): boolean {
    return this._stackEntries.size > 0;
  }

  /**
   * Get current stack size
   */
  public getStackSize(): number {
    if (this._stackEntries.size > 0) {
      return this._stackEntries.size;
    }
    return this._singlePanelType ? 1 : 0;
  }

  /**
   * Update state from router navigation
   */
  public update(router: Router, params: ParamMap) {
    this._buildStateFromParams(params);
    this._viewModel = router.getCurrentNavigation()?.extras?.state?.['viewModel'];
  }

  /**
   * Update state with explicit view model (for programmatic navigation)
   */
  public updateWithViewModel(params: ParamMap, viewModel?: SidePanelViewModel) {
    this._buildStateFromParams(params);
    this._viewModel = viewModel;
  }

  private _buildStateFromParams(params: ParamMap) {
    // Reset state
    this._integration = params.get(QueryParams.integration);
    this._stackEntries.clear();
    this._singlePanelType = null;
    this._singlePanelId = null;

    // Parse stack params (s0, s1, s2...)
    for (const key of params.keys) {
      if (isStackParam(key)) {
        const index = getStackParamIndex(key);
        const value = params.get(key);
        if (index >= 0 && value) {
          const parsed = parseStackEntry(value);
          if (parsed && isStackablePanelType(parsed.type)) {
            this._stackEntries.set(index, parsed);
          }
        }
      }
    }

    // If no stack params, check for single panel format (ONLY resource and violation)
    if (this._stackEntries.size === 0) {
      const resourceId = params.get(QueryParams.resource);
      const violationId = params.get(QueryParams.violation);

      if (resourceId) {
        this._singlePanelType = 'resource';
        this._singlePanelId = resourceId;
      } else if (violationId) {
        this._singlePanelType = 'violation';
        this._singlePanelId = violationId;
      }
    }
  }

  private _buildStackedPanels(): SidePanelConfig[] {
    const panels: SidePanelConfig[] = [];

    // Sort by index to ensure correct order
    const sortedEntries = [...this._stackEntries.entries()].sort((a, b) => a[0] - b[0]);

    for (const [index, entry] of sortedEntries) {
      const registryEntry = PANEL_REGISTRY[entry.type];
      if (registryEntry) {
        panels.push({
          component: registryEntry.component,
          inputs: registryEntry.inputs(entry.id, this._integration!, this._viewModel),
          shortcuts: registryEntry.shortcuts,
          stackKey: `s${index}`,
        });
      }
    }

    return panels;
  }

  private _buildSinglePanel(): SidePanelConfig[] {
    if (!this._singlePanelType || !this._singlePanelId) {
      return [];
    }

    const registryEntry = PANEL_REGISTRY[this._singlePanelType];
    if (!registryEntry) {
      return [];
    }

    return [
      {
        component: registryEntry.component,
        inputs: registryEntry.inputs(this._singlePanelId, this._integration!, this._viewModel),
        shortcuts: registryEntry.shortcuts,
        stackKey: this._singlePanelType,
      },
    ];
  }
}
